/** Where a dragged row lands relative to the target row. */
export type DataGridRowDropPosition = "before" | "after" | "into";

/** A finished row drag, emitted by `rowReorder.onRowMove`. */
export interface DataGridRowMove {
    rowId: string;
    targetRowId: string;
    /** `into` (tree tables) makes the row a child of the target. */
    position: DataGridRowDropPosition;
}

/** A shown row as the drop logic sees it, in display order. */
export interface DropRow {
    id: string;
    depth: number;
    parentId: string | null;
    /** Expanded with children shown right below it. */
    hasShownChildren: boolean;
    hasChildren: boolean;
}

/** A drop target plus where its indicator goes. */
export interface RowDropTarget extends DataGridRowMove {
    /** Display index of the target row. */
    index: number;
    /**
     * Display index of the row the indicator draws against: the last shown
     * descendant for `after` an expanded row, else `index`.
     */
    indicatorIndex: number;
    /** Indent of the indicator: the depth the row would land at. */
    depth: number;
}

interface DropContext {
    rows: readonly DropRow[];
    sourceIndex: number;
    isTree: boolean;
    canDrop?: (move: DataGridRowMove) => boolean;
}

/** One past the source's last shown descendant. */
const getSubtreeEnd = (rows: readonly DropRow[], sourceIndex: number) => {
    const sourceDepth = rows[sourceIndex]?.depth ?? 0;
    let end = sourceIndex + 1;
    while ((rows[end]?.depth ?? -1) > sourceDepth) {
        end += 1;
    }
    return end;
};

const getPreviousSiblingIndex = (
    rows: readonly DropRow[],
    sourceIndex: number,
) => {
    const source = rows[sourceIndex];
    for (let index = sourceIndex - 1; index >= 0; index -= 1) {
        const row = rows[index];
        if (!source || !row || row.depth < source.depth) {
            return -1;
        }
        if (row.depth === source.depth) {
            return row.parentId === source.parentId ? index : -1;
        }
    }
    return -1;
};

const toTarget = (
    rows: readonly DropRow[],
    {
        sourceIndex,
        index,
        position,
    }: {
        sourceIndex: number;
        index: number;
        position: DataGridRowDropPosition;
    },
): RowDropTarget | null => {
    const source = rows[sourceIndex];
    const row = rows[index];
    if (!source || !row) {
        return null;
    }
    return {
        rowId: source.id,
        targetRowId: row.id,
        position,
        index,
        indicatorIndex:
            position === "after" ? getSubtreeEnd(rows, index) - 1 : index,
        depth: position === "into" ? row.depth + 1 : row.depth,
    };
};

/**
 * Whether dropping there changes anything: never onto the source or its
 * own subtree, never back where it already is, and only where `canDrop`
 * agrees. Only rows that already have children take `into` unless `canDrop`
 * opts the row in.
 */
export const isUsefulTarget = (
    context: DropContext,
    target: RowDropTarget,
): boolean => {
    const { rows, sourceIndex, canDrop } = context;
    const source = rows[sourceIndex];
    const row = rows[target.index];
    if (!source || !row) {
        return false;
    }
    const subtreeEnd = getSubtreeEnd(rows, sourceIndex);
    if (target.index >= sourceIndex && target.index < subtreeEnd) {
        return false;
    }
    const isNoop =
        (target.position === "before" &&
            target.index === subtreeEnd &&
            row.parentId === source.parentId) ||
        (target.position === "after" &&
            target.index === getPreviousSiblingIndex(rows, sourceIndex)) ||
        (target.position === "into" && row.id === source.parentId);
    if (isNoop) {
        return false;
    }
    if (target.position === "into" && !row.hasChildren) {
        return canDrop?.(target) === true;
    }
    return canDrop?.(target) ?? true;
};

/** `after` a parent whose children show reads as above them; use `before` its first child. */
const getAfterTarget = (context: DropContext, index: number) => {
    const row = context.rows[index];
    return row?.hasShownChildren
        ? toTarget(context.rows, {
              sourceIndex: context.sourceIndex,
              index: index + 1,
              position: "before",
          })
        : toTarget(context.rows, {
              sourceIndex: context.sourceIndex,
              index,
              position: "after",
          });
};

/**
 * The target under the pointer, from the hovered row and how far down it the
 * pointer is (0 to 1). Flat tables split each row in halves; tree tables
 * keep the middle half for dropping into the row.
 */
export const getPointerDropTarget = (
    context: DropContext,
    { index, fraction }: { index: number; fraction: number },
): RowDropTarget | null => {
    const { sourceIndex, rows, isTree } = context;
    const edge = isTree ? 0.25 : 0.5;
    const before = toTarget(rows, { sourceIndex, index, position: "before" });
    const after = getAfterTarget(context, index);
    const into = toTarget(rows, { sourceIndex, index, position: "into" });
    const nearerEdge = fraction < 0.5 ? before : after;
    let candidates = [into, nearerEdge];
    if (fraction < edge || fraction > 1 - edge || !isTree) {
        candidates = [nearerEdge];
    }
    return (
        candidates.find(
            (target): target is RowDropTarget =>
                target !== null && isUsefulTarget(context, target),
        ) ?? null
    );
};

/**
 * The row and its ancestors whose shown subtrees end at `index`, deepest
 * first: each can take an `after` drop below that row.
 */
const getClosingRowIndexes = (
    rows: readonly DropRow[],
    { index, indexById }: { index: number; indexById: Map<string, number> },
) => {
    const nextDepth = rows[index + 1]?.depth ?? 0;
    const indexes: number[] = [];
    let current: number | undefined = index;
    while (current !== undefined && rows[current].depth >= nextDepth) {
        indexes.push(current);
        const parentId: string | null = rows[current].parentId;
        current = parentId === null ? undefined : indexById.get(parentId);
    }
    return indexes;
};

/**
 * Every distinct useful target top to bottom, for keyboard dragging. An
 * `after` that lands where the next row's `before` does is left out. Where
 * expanded subtrees end, an `after` for each closing level, so a row can land
 * below an expanded parent without collapsing it.
 */
export const getKeyboardDropTargets = (
    context: DropContext,
): RowDropTarget[] => {
    const { rows, sourceIndex, isTree } = context;
    const indexById = new Map(rows.map((row, index) => [row.id, index]));
    const targets: RowDropTarget[] = [];
    const push = (target: RowDropTarget | null) => {
        if (target && isUsefulTarget(context, target)) {
            targets.push(target);
        }
    };
    rows.forEach((row, index) => {
        push(toTarget(rows, { sourceIndex, index, position: "before" }));
        if (isTree) {
            push(toTarget(rows, { sourceIndex, index, position: "into" }));
        }
        if (row.hasShownChildren) {
            return;
        }
        const next = rows[index + 1];
        for (const closingIndex of getClosingRowIndexes(rows, {
            index,
            indexById,
        })) {
            if (next?.parentId !== rows[closingIndex].parentId) {
                push(
                    toTarget(rows, {
                        sourceIndex,
                        index: closingIndex,
                        position: "after",
                    }),
                );
            }
        }
    });
    return targets;
};

const POSITION_OFFSETS: Record<DataGridRowDropPosition, number> = {
    before: -0.5,
    into: 0,
    after: 0.5,
};

/** Position of a target in rows, for ordering it against the source. */
const getTargetOffset = (target: RowDropTarget) =>
    target.indicatorIndex + POSITION_OFFSETS[target.position];

/**
 * The keyboard target after one arrow press: the neighbour of the current
 * target, or from the source's own place on the first press.
 */
export const stepKeyboardTarget = ({
    targets,
    current,
    sourceIndex,
    direction,
}: {
    targets: readonly RowDropTarget[];
    current: RowDropTarget | null;
    sourceIndex: number;
    direction: 1 | -1;
}): RowDropTarget | null => {
    if (current) {
        const index = targets.findIndex(
            (target) =>
                target.targetRowId === current.targetRowId &&
                target.position === current.position,
        );
        return targets[index + direction] ?? current;
    }
    const ordered = direction === 1 ? targets : [...targets].reverse();
    return (
        ordered.find((target) =>
            direction === 1
                ? getTargetOffset(target) > sourceIndex
                : getTargetOffset(target) < sourceIndex,
        ) ?? null
    );
};
